import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Map as MapIcon, Info, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GEO_URL = '/br-states.geojson';

const MODES = [
  { key: 'governador', label: 'Governadores' },
  { key: 'presidente', label: 'Presidente' },
  { key: 'deputados_federais', label: 'Deputados Federais' },
];

const BrazilElectionMap = ({ year = 2022 }) => {
  const [mode, setMode] = useState('governador');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const yearNum = parseInt(year);
  const supportsMap = [2022, 2018].includes(yearNum);

  useEffect(() => {
    if (!supportsMap) { setData(null); return; }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const resp = await axios.get(`${API}/elections/map-data`, {
          params: { year: yearNum, mode },
          timeout: 120000,
        });
        if (!cancelled) setData(resp.data);
      } catch {
        if (!cancelled) setData({ map: [], message: 'Erro ao carregar dados do mapa.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [yearNum, mode, supportsMap]);

  const byUf = useMemo(() => {
    const out = {};
    (data?.map || []).forEach(r => { if (r.uf) out[r.uf] = r; });
    return out;
  }, [data]);

  if (!supportsMap) {
    return (
      <Card className="p-4 md:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="brazil-map">
        <h3 className="text-base md:text-lg font-bold text-[#0F1C2E] flex items-center gap-2 mb-2">
          <MapIcon className="w-5 h-5 text-[#006030]" /> Mapa Eleitoral do Brasil
        </h3>
        <div className="flex items-start gap-2 p-3 bg-[#F4F6F8] border border-[#E2E8F0] rounded-md text-xs text-[#94A3B8]">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Disponível apenas para eleições gerais (2022 e 2018). Selecione um ano compatível acima.</span>
        </div>
      </Card>
    );
  }

  const legend = data?.legend || [];

  return (
    <Card className="p-4 md:p-5 bg-white border border-[#E2E8F0] rounded-md relative" data-testid="brazil-map">
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div>
          <h3 className="text-base md:text-lg font-bold text-[#0F1C2E] flex items-center gap-2">
            <MapIcon className="w-5 h-5 text-[#006030]" /> Mapa Eleitoral do Brasil
          </h3>
          <p className="text-xs text-[#94A3B8] mt-0.5">
            {yearNum} · Passe o cursor sobre um estado para ver detalhes.
          </p>
        </div>
        <Badge className="bg-[#006030] text-white text-[10px] shrink-0">TSE oficial</Badge>
      </div>

      {/* Mode toggle */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="map-mode-toggle">
        {MODES.map(m => (
          <Button
            key={m.key}
            size="sm"
            variant={mode === m.key ? 'default' : 'outline'}
            onClick={() => setMode(m.key)}
            className={
              mode === m.key
                ? 'bg-[#006030] hover:bg-[#004D26] text-white text-xs h-8'
                : 'border-[#E2E8F0] text-[#475569] text-xs h-8'
            }
            data-testid={`map-mode-${m.key}`}
          >
            {m.label}
          </Button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#006030] mr-3" />
          <span className="text-sm text-[#94A3B8]">Carregando mapa do TSE...</span>
        </div>
      )}

      {!loading && (
        <div className="relative" onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 900, center: [-54, -15] }}
            width={800}
            height={520}
            style={{ width: '100%', height: 'auto' }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const uf = geo.properties.sigla || geo.properties.id;
                  const info = byUf[uf];
                  const fill = info?.color || '#E2E8F0';
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fill}
                      stroke="#FFFFFF"
                      strokeWidth={0.6}
                      onMouseEnter={() => setHovered({ uf, name: geo.properties.name, info })}
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        default: { outline: 'none', transition: 'fill 0.2s' },
                        hover: { fill: '#0F1C2E', outline: 'none', cursor: 'pointer' },
                        pressed: { outline: 'none' },
                      }}
                      data-testid={`map-state-${uf}`}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>

          {/* Tooltip */}
          {hovered && (
            <div
              className="fixed z-50 pointer-events-none bg-[#0F1C2E] text-white text-xs px-3 py-2 rounded-md shadow-xl border border-[#475569]"
              style={{
                left: Math.min(tooltipPos.x + 12, window.innerWidth - 220),
                top: Math.min(tooltipPos.y + 12, window.innerHeight - 120),
                maxWidth: 210,
              }}
              data-testid="map-tooltip"
            >
              <p className="font-bold">{hovered.name} ({hovered.uf})</p>
              {hovered.info ? (
                <>
                  {hovered.info.nome && <p className="mt-1">{hovered.info.nome}</p>}
                  <p className="flex items-center gap-1.5 mt-1">
                    <span
                      className="inline-block w-3 h-3 rounded-sm"
                      style={{ backgroundColor: hovered.info.color }}
                    />
                    <span className="font-semibold">{hovered.info.partido}</span>
                    <span className="opacity-70">· {hovered.info.cargo}</span>
                  </p>
                  {hovered.info.assentos_dominante != null && (
                    <p className="opacity-70 mt-1">
                      {hovered.info.assentos_dominante}/{hovered.info.total_eleitos} deputados
                    </p>
                  )}
                </>
              ) : (
                <p className="opacity-70 mt-1">Sem dados</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Legenda */}
      {!loading && legend.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="map-legend">
          {legend.map(l => (
            <div
              key={l.partido}
              className="flex items-center gap-1.5 text-[11px] text-[#475569] bg-[#F4F6F8] px-2 py-1 rounded-md border border-[#E2E8F0]"
            >
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: l.color }} />
              <span className="font-medium">{l.partido}</span>
            </div>
          ))}
        </div>
      )}

      {!loading && data?.fonte && (
        <div className="mt-3 flex items-center justify-end">
          <a
            href={data.fonte_url || 'https://divulgacandcontas.tse.jus.br/'}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] text-[#006030] hover:underline flex items-center gap-1"
          >
            Fonte: {data.fonte} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </Card>
  );
};

export default BrazilElectionMap;
